import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import {
  Sachverhalt,
  SachverhaltErstellen,
  SachverhaltField,
  SachverhaltHeader,
  SachverhaltSet,
} from '@models/dossier/dossier-models';
import { select, Store } from '@ngrx/store';
import { IServerValidationErrors } from 'app/core/services/validation/models/IServerValidationErrors';
import { ValidationService } from 'app/core/services/validation/validation.service';
import { getLastSelectedMerkmal } from 'app/modules/shared-area/ducks/merkmal/store';
import {
  getSachverhalt,
  getSachverhaltEditValues,
  isSachverhaltLoading,
  serverValidationErrors,
} from 'app/modules/shared-area/entry-components/sachverhalt/store/sachverhalt.selectors';
import { IAppState } from 'app/modules/shared-area/root-state';
import { EMPTY, Observable, Subject } from 'rxjs';
import { first, takeUntil, tap } from 'rxjs/operators';
import * as SachverhaltActions from '../store/sachverhalt.actions';
import { UserProfileEffects } from '../../../ducks/user-profile/store/user-profile.effects';
import { ToastService } from 'app/core/services/toast-service';
import * as _ from 'lodash';
import { DossierService } from 'app/modules/dossier-area/services/dossier/dossier.service';

@Component({
  selector: 'app-sachverhalt-create',
  templateUrl: './sachverhalt-create.component.html',
})
export class SachverhaltCreateComponent implements OnInit, OnDestroy, AfterViewInit {
  private id: string;
  public sachverhaltId?: number;
  public erfassungSachverhaltId?: number;
  private merkmal: string;

  public sachverhalt: Sachverhalt;
  public header: SachverhaltHeader;
  public fields: SachverhaltField[];
  public values: SachverhaltSet;

  duplicatedValues: SachverhaltSet;
  public isBatchMode = false;
  public message: string;

  public IsFormInitialized = false;
  public IsSaving: boolean;
  public emptyFields: string[];
  public serverValidationErrors$: Observable<IServerValidationErrors> = EMPTY;

  public isSachverhaltLoading$: Observable<boolean>;
  private destroy$ = new Subject<void>();

  @Output()
  public closeWindow: EventEmitter<any | null> = new EventEmitter<any | null>();

  constructor(
    private store: Store<IAppState>,
    private toastyService: ToastService,
    private validation: ValidationService,
    private userProfileEffects: UserProfileEffects,
    private dossierService: DossierService,
  ) {
    this.IsSaving = false;
    this.emptyFields = [];
  }

  ngOnInit() {
    this.duplicatedValues = null;
    this.isSachverhaltLoading$ = this.store.pipe(select(isSachverhaltLoading));
    
    this.store
      .pipe(
        select(getLastSelectedMerkmal),
        tap(selectedMerkmal => (this.merkmal = selectedMerkmal)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.store.pipe(select(getSachverhaltEditValues), first()).subscribe(values => {
      this.id = values.id;
      this.sachverhaltId = values.sachverhaltId;
      this.erfassungSachverhaltId = values.erfassungSachverhaltId;
    });

    if (this.merkmal) {
      this.loadSachverhalt();
    } else {
      this.duplicateMerkmal();
    }

    this.serverValidationErrors$ = this.store.pipe(select(serverValidationErrors));
  }

  private loadSachverhalt() {
    this.store.dispatch(SachverhaltActions.loadSachverhaltEffect({
      param: {
        id: this.id,
        merkmal: this.merkmal,
      }
    }));
  }

  duplicateMerkmal() {
    this.store.dispatch(SachverhaltActions.startLoadingSachverhalt());
    if (!_.isNil(this.sachverhaltId)) {
      this.dossierService
        .DossierSachverhaltBearbeitenAnzeigen(this.id, this.sachverhaltId)
        .subscribe(sachverhalt => this.setMerkmalAndDuplicatedValuesAndLoadSachverhalt(sachverhalt));
    } else if (!_.isNil(this.erfassungSachverhaltId)) {
      this.dossierService
        .DossierNeuenSachverhaltBearbeitenAnzeigen(this.id, this.erfassungSachverhaltId)
        .subscribe(sachverhalt => this.setMerkmalAndDuplicatedValuesAndLoadSachverhalt(sachverhalt));
    }
  }

  private setMerkmalAndDuplicatedValuesAndLoadSachverhalt(sachverhalt: Sachverhalt) {
    this.merkmal = sachverhalt.header.merkmalName;
    this.duplicatedValues = sachverhalt.values;
    // Entfernen alle "_originalLfdSubsetNr" aus allen Subsets, damit das Backend die Daten auch
    // als neues Subset interpretiert
    for (const key in this.duplicatedValues) {
      const value = this.duplicatedValues[key];
      if (key.startsWith('_SUBSET_') && Array.isArray(value)) {
        for (const n in value) {
          delete this.duplicatedValues[key][n]['_originalLfdSubsetNr'];
        }
      }
    }
    this.loadSachverhalt();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.store
      .pipe(
        select(getSachverhalt),
        takeUntil(this.destroy$)
      )
      .subscribe(sachverhalt => {
        if (sachverhalt) {
          this.store.dispatch(SachverhaltActions.resetServerValidationError());
          this.sachverhalt = sachverhalt;
          this.header = sachverhalt.header;
          this.fields = sachverhalt.fields;
          this.values = this.duplicatedValues || sachverhalt.values;
          this.IsFormInitialized = true;
        }
      });
  }

  get isFormValid(): boolean {
    const emptyMandatoryFieldCount = this.validation.checkForRequiredFields(this.fields, this.values).length;
    const invalidLookupFieldCount = this.validation.checkLookupFields(this.fields, this.values).length;
    return emptyMandatoryFieldCount === 0 && invalidLookupFieldCount === 0;
  }

  public neuenSachverhaltErstellenUndWeitereAnlegen() {
    const emptyRequiredFields = this.validation.checkForRequiredFields(this.fields, this.values);
    const invalidLookupFields = this.validation.checkLookupFields(this.fields, this.values);
    if (emptyRequiredFields.length === 0 && invalidLookupFields.length === 0) {
      const erstellen: SachverhaltErstellen = new SachverhaltErstellen(this.id, this.merkmal, this.values, true);
      this.userProfileEffects.addFavouriteMerkmalAsync({
        key: this.merkmal,
        displayName: this.sachverhalt.header.merkmal,
      });
      this.store.dispatch(SachverhaltActions.createSachverhaltAndOpen({ payload: erstellen }));
    } else {
      this.emptyFields = emptyRequiredFields.concat(invalidLookupFields);
      if (emptyRequiredFields.length > 0) {
        this.toastyService.toastWarning('validation.titles.error', 'validation.messages.notAllRequiredFieldsAreSet');
      }
      if (invalidLookupFields.length > 0) {
        this.toastyService.toastWarning('validation.titles.error', 'validation.messages.invalidLookupFieldValues');
      }
    }
  }

  public neuenSachverhaltErstellenUndSchliessen() {
    const emptyRequiredFields = this.validation.checkForRequiredFields(this.fields, this.values);
    const invalidLookupFields = this.validation.checkLookupFields(this.fields, this.values);
    if (emptyRequiredFields.length === 0 && invalidLookupFields.length === 0) {
      const erstellen: SachverhaltErstellen = new SachverhaltErstellen(this.id, this.merkmal, this.values);
      this.userProfileEffects.addFavouriteMerkmalAsync({
        key: this.merkmal,
        displayName: this.sachverhalt.header.merkmal,
      });
      this.store.dispatch(SachverhaltActions.createSachverhaltAndClose({ payload: erstellen }));
    } else {
      this.emptyFields = emptyRequiredFields.concat(invalidLookupFields);
      if (emptyRequiredFields.length > 0) {
        this.toastyService.toastWarning('validation.titles.error', 'validation.messages.notAllRequiredFieldsAreSet');
      }
      if (invalidLookupFields.length > 0) {
        this.toastyService.toastWarning('validation.titles.error', 'validation.messages.invalidLookupFieldValues');
      }
    }
  }

  public close() {
    this.closeWindow.emit(null);
  }
}
